/**
 * Type definitions for Message Simulator components
 * @module MessageSimulator/types
 */

/**
 * Status of a history entry
 * @typedef {'success' | 'error' | 'timeout'} HistoryEntryStatus
 */

/**
 * Represents a single message/response record in the history
 * @typedef {Object} HistoryEntry
 * @property {string} id - Unique identifier for the entry
 * @property {string} timestamp - ISO 8601 timestamp when the response was received
 * @property {string} topic - The MQTT topic string
 * @property {string} messageType - Description of the message type (e.g., "Device Telemetry")
 * @property {number} topicNumber - The topic number
 * @property {Object} sentPayload - The payload that was sent to the device
 * @property {Object|null} responsePayload - The response received from the device (null if error/timeout)
 * @property {HistoryEntryStatus} status - Status of the message exchange
 * @property {string} [errorMessage] - Error message if status is 'error'
 */

/**
 * Maximum number of history entries to retain
 * @constant {number}
 */
export const MAX_HISTORY_SIZE = 50;

/**
 * Creates a new history entry
 * @param {Object} params - Entry parameters
 * @param {string} params.topic - The MQTT topic string
 * @param {string} params.messageType - Description of the message type
 * @param {number} params.topicNumber - The topic number
 * @param {Object} params.sentPayload - The payload that was sent
 * @param {Object|null} params.responsePayload - The response received
 * @param {HistoryEntryStatus} params.status - Status of the exchange
 * @param {string} [params.errorMessage] - Error message if applicable
 * @returns {HistoryEntry} A new history entry
 */
export const createHistoryEntry = ({
  topic,
  messageType,
  topicNumber,
  sentPayload,
  responsePayload,
  status,
  errorMessage,
}) => ({
  id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  timestamp: new Date().toISOString(),
  topic,
  messageType,
  topicNumber,
  sentPayload,
  responsePayload,
  status,
  ...(errorMessage && { errorMessage }),
});
